import React, { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';

import { COLORS } from 'Components/foundation';
import { withTooltip } from 'Components/hoc';
import UDropdownBox from 'Components/unit/UDropdownBox/UDropdownBox';
import UDropdownItem from 'Components/unit/UDropdownItem/UDropdownItem';
import UFlag from 'Components/unit/UFlag/UFlag';
import UIcon from 'Components/unit/UIcon/UIcon';
import UIconButton from 'Components/unit/UIconButton/UIconButton';
import UProgressBar from 'Components/unit/UProgressBar/UProgressBar';

import styles from './ULanguageListItem.style';

export type OptionType = {
  title: string;
  callback: () => void;
  color?: string;
};

export type ULanguageListItemProps = {
  countryCode: string;
  label: string;
  isDefault: boolean;
  defaultLabel: string;
  progressLabel?: string;
  progress?: number;
  options: OptionType[];
  style?: CSSProperties;
  optionsDisabled: boolean;
  optionsTooltipText: string;
};

const WrappedCompUIconButton = withTooltip(UIconButton);

export const ULanguageListItem = ({
  countryCode,
  label,
  isDefault,
  defaultLabel = 'default',
  progressLabel = '',
  progress,
  options,
  style,
  optionsDisabled = false,
  optionsTooltipText = '',
}: ULanguageListItemProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isDropdownBoxOpen, setIsDropdownBoxOpen] = useState(false);

  const wrapperStyle = {
    ...styles.wrapper,
    ...(isDefault ? styles.wrapperDefault : {}),
    ...style,
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside, false);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, false);
    };
  }, []);

  const handleClickItemOption = useCallback(
    (callback: () => void) => () => {
      setIsDropdownBoxOpen(false);
      callback();
    },
    [],
  );

  const handleClickOptions = () => {
    if (!optionsDisabled) setIsDropdownBoxOpen(!isDropdownBoxOpen);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (wrapperRef.current && event.target instanceof Node && !wrapperRef.current.contains(event.target))
      setIsDropdownBoxOpen(false);
  };
  const renderOption = ({ title, callback, color = COLORS.TEXT.DEFAULT }: OptionType) => {
    return <UDropdownItem key={title} text={title} onClick={handleClickItemOption(callback)} color={color} />;
  };

  return (
    <div data-testid="language-list-item" style={wrapperStyle}>
      <div key={countryCode} style={styles.containerLanguage}>
        <div style={styles.containerFlagLabel}>
          <UFlag countryCode={countryCode} size={20} />
          <div style={styles.containerLabel}>
            <span style={styles.label} data-testid="language-label">
              {label}
            </span>
            {isDefault ? (
              <span style={styles.defaultLabel} data-testid="default-label">{`(${defaultLabel})`}</span>
            ) : null}
          </div>
        </div>
        <div ref={wrapperRef} style={styles.containerIcon}>
          {isDefault ? (
            <UIcon name="lock" size={16} color={COLORS.TEXT.DISABLED} />
          ) : (
            <WrappedCompUIconButton
              tooltipPosition="left"
              tooltipContent={optionsTooltipText}
              tooltipEnabled={optionsDisabled}
              disabled={optionsDisabled}
              tooltipMaxWidth={400}
              icon="options"
              size="M"
              ghost
              onClick={handleClickOptions}
            />
          )}
          {isDropdownBoxOpen ? (
            <UDropdownBox items={options} renderItem={renderOption} style={styles.optionDropDown} />
          ) : null}
        </div>
      </div>
      {progress !== undefined ? (
        <UProgressBar percent={progress} label={progressLabel} style={styles.containerProgressBar} />
      ) : null}
    </div>
  );
};

export default ULanguageListItem;
